import React from 'react';
import ReactDOM from 'react-dom';
import { Box, Paper, IconButton, Typography, List, ListItem, Link, Button, makeStyles } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { TEAL_PRIMARY } from 'src/common-ui/theme';
import { logError } from 'src/services/loggingService';

export interface DebugInfo {
  mainComponentType: string;
  isWorklist: boolean;
  worklistTabComponentType?: string;
  configFilePaths: string[];
  pivotFilePaths: string[];
  currentTime: string;
  favoriteActive: boolean;
  favoriteName?: string;
  favoriteId?: string;
  userEmail?: string;
  userId?: string;
  scope?: string;
  filters?: Record<string, string[]>;
}

interface DebugPopperProps {
  info: DebugInfo;
  onClose: () => void;
}

const useStyles = makeStyles({
  popperContainer: {
    position: 'fixed',
    top: '16px',
    right: '16px',
    minWidth: '350px',
    maxHeight: '80vh',
    overflowY: 'auto',
    zIndex: 10000,
  },
  paper: {
    backgroundColor: 'rgba(255,255,255,0.9)',
    border: '1px solid #ccc',
    borderRadius: '4px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 12px',
    borderBottom: '1px solid #ddd',
  },
  content: {
    padding: '12px',
    fontFamily: 'Arial, sans-serif',
    fontSize: '14px',
    lineHeight: 1.4,
    color: '#333',
  },
  sectionTitle: {
    margin: '16px 0 8px',
    fontWeight: 700,
    fontSize: '14px',
    borderBottom: '1px solid #eee',
    paddingBottom: '4px',
  },
  list: {
    listStyle: 'none',
    paddingLeft: 0,
    margin: 0,
  },
  listItem: {
    padding: '4px 0',
    display: 'flex',
    alignItems: 'center',
  },
  link: {
    display: 'inline-flex',
    alignItems: 'center',
    color: TEAL_PRIMARY,
    textDecoration: 'none',
    fontSize: '14px',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  icon: {
    marginRight: '6px',
    fontSize: '16px',
  },
  infoBlock: {
    marginBottom: '12px',
  },
  copyGroup: {
    display: 'flex',
    gap: '8px',
    marginTop: '16px',
  },
});

export const DebugPopper: React.FC<DebugPopperProps> = ({ info, onClose }) => {
  const classes = useStyles();
  const deployment = window.location.origin;

  const makeLink = (type: 'view' | 'model', id: string) => {
    const fileExt = type === 'view' ? '.viewdefn' : '.modeldefn';
    const path = `/uidefn/${type}/${encodeURIComponent(id)}${fileExt}`;
    const queryString = `deployment=${encodeURIComponent(deployment)}&path=${encodeURIComponent(path)}`;
    return `vscode://s5stratos.multipass/deeplink?${queryString}`;
  };

  const copyModalText = async () => {
    const lines: string[] = [];
    lines.push(`Component: ${info.mainComponentType}`);
    if (info.isWorklist && info.worklistTabComponentType) {
      lines.push(`Worklist Tab: ${info.worklistTabComponentType}`);
    }
    lines.push(`Config Files:`);
    if (info.configFilePaths.length) {
      info.configFilePaths.forEach((p) => lines.push(`  - ${p}`));
    } else {
      lines.push(`  (none)`);
    }
    lines.push(`Pivot Files:`);
    if (info.pivotFilePaths.length) {
      info.pivotFilePaths.forEach((p) => lines.push(`  - ${p}`));
    } else {
      lines.push(`  (none)`);
    }

    if (info.filters && Object.keys(info.filters).length) {
      lines.push(`Filters:`);
      Object.entries(info.filters).forEach(([name, vals]) => lines.push(`  • ${name}: [${vals.join(', ')}]`));
    } else {
      lines.push(`Filters: (none)`);
    }

    lines.push(`Active Favorite:`);
    if (info.favoriteActive) {
      lines.push(`  Name: ${info.favoriteName}`);
      lines.push(`  ID:   ${info.favoriteId}`);
    } else {
      lines.push(`  No selected favorite`);
    }

    if (info.userEmail) lines.push(`User Email: ${info.userEmail}`);
    if (info.userId) lines.push(`User ID:    ${info.userId}`);

    lines.push(`Time: ${info.currentTime}`);

    try {
      await navigator.clipboard.writeText(lines.join('\n'));
    } catch (err) {
      logError('Failed to copy modal text', err);
    }
  };

  const copyTicketInfo = async () => {
    const page = window.location.href;

    let filtersText: string;
    const entries = Object.entries(info.filters || {});
    if (entries.length) {
      // multi‐line, one bullet per filter
      filtersText = ['Filters:'].concat(entries.map(([name, ids]) => `  • ${name}: [${ids.join(', ')}]`)).join('\n');
    } else {
      filtersText = 'Filters: (none)';
    }
    const lines = [
      `Page: ${page}`,
      `Scope: ${info.scope}`,
      filtersText,
      info.favoriteActive ? `Favorite Name: ${info.favoriteName}` : `Favorite: (none)`,
      info.favoriteActive && info.favoriteId ? `Favorite ID:   ${info.favoriteId}` : '',
      info.userEmail ? `User Email: ${info.userEmail}` : '',
      info.userId ? `User ID: ${info.userId}` : '',
      `Time: ${info.currentTime}`,
    ].filter(Boolean);

    try {
      await navigator.clipboard.writeText(lines.join('\n'));
    } catch (err) {
      logError('Failed to copy ticket info', err);
    }
  };

  return ReactDOM.createPortal(
    <Box className={classes.popperContainer}>
      <Paper elevation={4} className={classes.paper}>
        <Box className={classes.header}>
          <Typography variant="subtitle1">Debug Info</Typography>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box className={classes.content}>
          <Box className={classes.infoBlock}>
            <Typography variant="body2">
              <strong>Component:</strong> {info.mainComponentType}
            </Typography>
            {info.isWorklist && info.worklistTabComponentType && (
              <Typography variant="body2">
                <strong>Worklist Tab:</strong> {info.worklistTabComponentType}
              </Typography>
            )}
          </Box>

          <Box className={classes.infoBlock}>
            <Typography className={classes.sectionTitle}>Config Files</Typography>
            {info.configFilePaths.length > 0 ? (
              <List dense className={classes.list}>
                {info.configFilePaths.map((id) => (
                  <ListItem key={id} className={classes.listItem}>
                    <Link href={makeLink('view', id)} target="_blank" rel="noopener" className={classes.link}>
                      <OpenInNewIcon className={classes.icon} />
                      {id}
                    </Link>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="caption" color="textSecondary">
                (none)
              </Typography>
            )}
          </Box>

          <Box className={classes.infoBlock}>
            <Typography className={classes.sectionTitle}>Pivot Files</Typography>
            {info.pivotFilePaths.length > 0 ? (
              <List dense className={classes.list}>
                {info.pivotFilePaths.map((id) => (
                  <ListItem key={id} className={classes.listItem}>
                    <Link href={makeLink('model', id)} target="_blank" rel="noopener" className={classes.link}>
                      <OpenInNewIcon className={classes.icon} />
                      {id}
                    </Link>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="caption" color="textSecondary">
                (none)
              </Typography>
            )}
          </Box>
          {info.filters && Object.keys(info.filters).length > 0 && (
            <Box className={classes.infoBlock}>
              <Typography className={classes.sectionTitle}>Filters</Typography>
              {Object.entries(info.filters).map(([name, ids]) => (
                <Typography key={name} variant="body2">
                  <strong>{name}:</strong> {ids.join(', ')}
                </Typography>
              ))}
            </Box>
          )}
          <Box className={classes.infoBlock}>
            <Typography className={classes.sectionTitle}>Active Favorite</Typography>
            {info.favoriteActive ? (
              <>
                <Typography variant="body2">
                  <strong>Name:</strong> {info.favoriteName}
                </Typography>
                <Typography variant="body2">
                  <strong>ID:</strong> {info.favoriteId}
                </Typography>
              </>
            ) : (
              <Typography variant="body2" color="textSecondary">
                No selected favorite
              </Typography>
            )}
          </Box>

          {(info.userEmail || info.userId) && (
            <Box className={classes.infoBlock}>
              <Typography className={classes.sectionTitle}>User Info</Typography>
              {info.userEmail && (
                <Typography variant="body2">
                  <strong>Email:</strong> {info.userEmail}
                </Typography>
              )}
              {info.userId && (
                <Typography variant="body2">
                  <strong>ID:</strong> {info.userId}
                </Typography>
              )}
            </Box>
          )}

          <Box className={classes.infoBlock}>
            <Typography variant="body2">
              <strong>Time:</strong> {info.currentTime}
            </Typography>
          </Box>

          <Box className={classes.copyGroup}>
            <Button variant="outlined" size="small" fullWidth onClick={copyModalText}>
              Copy Modal Text
            </Button>
            <Button variant="outlined" size="small" fullWidth onClick={copyTicketInfo}>
              Copy Ticket Info
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>,
    document.body,
  );
};
